import { IClientResponse } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/client';
import { find, flatMap, groupBy, isArray } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import {
    ModelFractionalSharePolicy,
    OrderFilterType,
    OrderTransaction,
    OverExposedType,
    PositionType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Account,
    HoldingInfo,
    MiddlewareConfig,
    Model,
    ModelWithExtraInfo,
    Order,
    OrderWithExtraInfo,
    OverExposedAccount,
    Sdks,
} from '../types';
import { ModelDriftReportItem, RebalanceRecommendationItem } from '../types/DriftReport.type';
import { Portfolio, PortfolioWithExtraInfo, PositionWithExtraInfo } from '../types/Portfolio.type';
import {
    formatDriftReport,
    formatHoldingInfo,
    formatOrders,
    formatOverExposedAccounts,
    formatPortfolio,
    formatQuote,
} from '../utils/dataUtils';
import { divide, multiply, subtract, sumBy } from '../utils/numberUtils';
import { IBranchApi, IUserApi } from './interfaces';
import { IModelApi } from './interfaces/IModelApi';

@injectable()
export class ModelApi extends CachedResponseApi implements IModelApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private branchApi: IBranchApi,
        @inject(ApiInjectToken.UserApi) protected userApi: IUserApi,
    ) {
        super(sdks, config, cache);
    }

    async getModels(): Promise<ModelWithExtraInfo[]> {
        const data = await Promise.all([
            this.sdks.orbis.get('v2/advisory/model/rtbs', undefined, { urlQuery: 'models' }),
            this.sdks.orbis.get('v2/advisory/models', undefined, {
                params: { loadQuotes: true, loadAccounts: true },
            }),
            this.branchApi.getAccounts(),
        ]);

        const models = data[1].data as Model[];

        return models.map((model) => {
            const {
                balance = 0,
                cashOnAccount = 0,
                markToMarket = 0,
                sodCashBalance = 0,
                sodMarginBalance = 0,
                sodShortBalance = 0,
                totalDividendAmount = 0,
                totalDividendYield = 0,
            } = find(data[0].data, (item) => item.modelId === model.id) || {};

            const accountIds = model.accounts.map((account) => account.accountId);
            const accounts = data[2].filter((account) => accountIds.includes(account.accountId));

            model.components.forEach((component) => {
                component.quote = formatQuote(component.quote);
            });

            return {
                ...model,
                balance,
                cashOnAccount,
                markToMarket,
                sodCashBalance,
                sodMarginBalance,
                sodShortBalance,
                totalDividendAmount,
                totalDividendYield,
                accounts,
            };
        });
    }

    async getModel(modelId: number): Promise<ModelWithExtraInfo | null> {
        const data = await this.getModels();
        return data.find((model) => model.id === modelId) || null;
    }

    async getPortfolio(
        modelId: number,
        combined = true,
        symbol?: string,
        positionType?: PositionType,
    ): Promise<PortfolioWithExtraInfo> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.getModels(),
            this.sdks.orbis.get('v2/advisory/model/portfolios', undefined, {
                urlQuery: modelId !== 0 ? modelId : 'All',
                params: {
                    loadQuotes: true,
                    loadIndustries: true,
                    loadCost: !combined,
                    combined,
                },
            }),
        ]);

        const accounts = data[0];
        const model = data[1].find((model) => model.id === modelId);
        const portfolio = data[2].data as Portfolio;

        return formatPortfolio(portfolio, accounts, [], model?.balance || 0, { symbol, positionType: positionType });
    }

    async getOrders(
        modelId: number,
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
        symbol?: string,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.sdks.orbis.get('v2/advisory/models'),
            this.userApi.getUserInfo(),
            this.branchApi.getInventoryAccounts(),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.sdks.orbis.get('orders/model', undefined, {
                    urlQuery: [modelId, filter],
                    params: { loadQuotes: true, symbol },
                });
            }),
        ]);

        const models = (data[0] as IClientResponse).data as Model[];
        const accounts = data[1].accountList;
        const inventoryAccounts = data[2] as Account[];
        const orders = flatMap(data.slice(3) as IClientResponse[], (item) => item.data) as Order[];

        return formatOrders(orders, models, accounts, inventoryAccounts[0]);
    }

    async getOverExposedAccounts(
        modelId: number,
        threshold: number,
        type: OverExposedType,
    ): Promise<OverExposedAccount[]> {
        const { data } = await this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
            urlQuery: [type, 'model', modelId],
            params: { threshold },
        });
        const accounts = await this.branchApi.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }

    async getDriftReport(modelId: number, accountIds: number[] = []): Promise<ModelDriftReportItem[]> {
        const data = await Promise.all([this.getModel(modelId), this.getPortfolio(modelId, false)]);

        const model = data[0];
        const positions = data[1].entries;
        let combinedPositions: PositionWithExtraInfo[] = [];

        if (accountIds.length) {
            const accountPositions = positions.filter(
                (position) => accountIds.includes(position.accountId) && !position.short && !position.option,
            );

            if (accountPositions.length) {
                const positionsGroupedBySymbol = groupBy(accountPositions, (position) => position.symbol);
                combinedPositions = Object.values(positionsGroupedBySymbol).map((positions) =>
                    positions.reduce<PositionWithExtraInfo>((result, position, index) => {
                        if (index !== 0) {
                            result.value += position.value;
                            result.quantity += position.quantity;
                        }
                        return result;
                    }, positions[0]),
                );
            }
        } else {
            combinedPositions = (await this.getPortfolio(modelId, true)).entries;
        }

        if (!model) {
            return [];
        }

        return formatDriftReport(model, positions, combinedPositions);
    }

    async getHoldingInfo(modelId: number, symbol: string): Promise<HoldingInfo> {
        const data = await Promise.all([this.getModel(modelId), this.getPortfolio(modelId, true)]);

        const model = data[0];
        const positions = data[1].entries;
        const modelComponent = model?.components?.filter((component) => component.symbol === symbol)[0];

        return formatHoldingInfo(symbol, positions, model?.balance || 0, modelComponent?.percentage || 0);
    }

    /*
        Model rebalancing steps:
          - Get model buying power
          - Get model's account buying powers
          - Get model portfolio (uncombined, no short positions)
          - For positions that have target = 0: sell all
          - For positions that have actual > target: sell the difference proportionally
          - For positions that have actual < target: buy the difference proportionally
    */
    async getRebalanceRecommendations(
        modelId: number,
        targets: Record<string, number>,
        fractionalSharePolicy = ModelFractionalSharePolicy.RoundDown,
        accountIds: number[] = [],
        presets: {
            accountId: number;
            transaction: OrderTransaction.Buy | OrderTransaction.Sell;
            symbol: string;
            quantity: number;
        }[] = [],
    ): Promise<RebalanceRecommendationItem[]> {
        const model = await this.getModel(modelId);

        if (!model) {
            return [];
        }

        const results: RebalanceRecommendationItem[] = [];
        const [driftReportItems, modelPortfolio] = await Promise.all([
            this.getDriftReport(modelId),
            this.getPortfolio(modelId, false),
        ]);
        const buyingPowers = (
            await Promise.all(
                model.accounts.map((account) =>
                    this.sdks.orbis.get('user/balance', undefined, {
                        params: { accountId: account.accountId },
                    }),
                ),
            )
        ).map((item) => item.data.availableCash as number);
        const accounts = model.accounts
            .map((account, index) => ({
                ...account,
                buyingPower: buyingPowers[index],
            }))
            .reduce<Record<number, RebalanceRecommendationItem['account']>>((result, account) => {
                result[account.accountId] = account;
                return result;
            }, {});

        const positions = groupBy(
            modelPortfolio.entries.filter((position) => !position.short && !position.option),
            (item) => item.symbol,
        );

        // Calculations
        Object.keys(targets).forEach((symbol) => {
            const current = driftReportItems.find((item) => item.symbol === symbol);

            if (!current) {
                return;
            }

            const quote = current.quote;
            const expectedPrice = quote.mutualFund ? quote.navPrice : quote.lastPrice || 0;
            const target = targets[symbol];

            if (target === 0) {
                positions[symbol]?.forEach((position) => {
                    const positionWeight = divide(position?.value || 0, accounts[position.accountId]?.balance);
                    const sellValue = multiply(expectedPrice, position.quantity);

                    results.push({
                        account: accounts[position.accountId],
                        transaction: OrderTransaction.Sell,
                        quantity: position.quantity,
                        currentWeight: positionWeight,
                        targetWeight: target,
                        error: '',
                        expectedPrice: expectedPrice,
                        expectedValue: sellValue,
                        position,
                        symbol,
                        quote,
                    });

                    // accounts[position.accountId].buyingPower += sellValue;
                });
            } else {
                Object.values(accounts).forEach((account) => {
                    const position = positions[symbol]?.find((position) => position.accountId === account.accountId);
                    const positionWeight = divide(position?.value || 0, account.balance);

                    if (positionWeight === target || (accountIds.length && !accountIds.includes(account.accountId))) {
                        return;
                    }

                    const transaction = positionWeight > target ? OrderTransaction.Sell : OrderTransaction.Buy;
                    // const maxQuantity =
                    //     transaction === OrderTransaction.Sell
                    //         ? position?.quantity || 0
                    //         : divide(account.buyingPower, expectedPrice);
                    let expectedValue = multiply(account.balance, Math.abs(subtract(positionWeight, target)));
                    // let expectedQuantity = Math.min(maxQuantity, divide(expectedValue, expectedPrice));
                    let expectedQuantity = divide(expectedValue, expectedPrice);

                    if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                        expectedQuantity =
                            fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                                ? Math.floor(expectedQuantity)
                                : Math.ceil(expectedQuantity);
                        expectedValue = multiply(expectedQuantity, expectedPrice);
                    }

                    results.push({
                        account,
                        transaction,
                        quantity: expectedQuantity,
                        currentWeight: positionWeight,
                        targetWeight: target,
                        error: '',
                        expectedPrice,
                        expectedValue,
                        position,
                        symbol,
                        quote,
                    });

                    // accounts[account.accountId].buyingPower += expectedValue * (transaction === OrderTransaction.Buy ? -1 : transaction === OrderTransaction.Sell ? 1 : 0);
                });
            }
        });

        // Prevent NaN or Infinity
        results.forEach((item) => {
            item.quantity = item.quantity || 0;
            item.expectedValue = item.expectedValue || 0;
        });

        const resultsGroupedByAccount = groupBy(results, (item) => item.account.accountId);

        // Checking buying transactions to prevent buying more than buying power
        Object.keys(resultsGroupedByAccount).forEach((key) => {
            const accountId = Number(key);
            const buyingPower = accounts[accountId].buyingPower;
            const totalBuyingValue = sumBy(resultsGroupedByAccount[accountId], (item) =>
                item.transaction === OrderTransaction.Buy ? item.expectedValue : 0,
            );

            if (buyingPower < 0 || totalBuyingValue <= buyingPower) {
                return;
            }

            resultsGroupedByAccount[key].forEach((item) => {
                item.expectedValue = buyingPower;
                item.quantity = divide(item.expectedValue, item.expectedPrice);

                if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                    item.quantity =
                        fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                            ? Math.floor(item.quantity)
                            : Math.ceil(item.quantity);
                    item.expectedValue = multiply(item.quantity, item.expectedPrice);
                }
            });
        });

        presets.forEach((preset) => {
            const recommendation = results.find(
                (item) => item.account.accountId === preset.accountId && item.symbol === preset.symbol,
            );
            if (recommendation) {
                recommendation.quantity = preset.quantity;
                recommendation.expectedValue = multiply(preset.quantity, recommendation.expectedPrice);
                recommendation.transaction = preset.transaction;
            }
        });

        return results;
    }
}
